import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import LandingReconstruction from "@/pages/LandingReconstruction";
import BusinessProfile from "@/pages/BusinessProfile";
import PublicClientProfile from "@/pages/PublicClientProfile";
import RankTrustReviewsDirectory from "@/pages/RankTrustReviewsDirectory";
import TrustMethodology from "@/pages/TrustMethodology";
import RankTrustReviewModeration from "@/pages/RankTrustReviewModeration";
import BusinessTrustDashboard, { TrustEditorialQueue } from "@/pages/BusinessTrustDashboard";
import TrustProfileSetup from "@/pages/TrustProfileSetup";
import PlaybookLanding from "@/pages/PlaybookLanding";
import PlaybookDownload from "@/pages/PlaybookDownload";
import PlaybookManager from "@/pages/PlaybookManager";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import { Terms, Privacy } from "@/pages/Legal";
import Presell from "@/pages/Presell";
import Demo from "@/pages/Demo";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import RankTracker from "@/pages/RankTracker";
import GeoGrid from "@/pages/GeoGrid";
import AIAudit from "@/pages/AIAudit";
import ContentStudio from "@/pages/ContentStudio";
import ContentEngine from "@/pages/ContentEngine";
import Competitors from "@/pages/Competitors";
import ChatGPTVisibility from "@/pages/ChatGPTVisibility";
import Reviews from "@/pages/Reviews";
import ReviewRequests from "@/pages/ReviewRequests";
import PublicReview from "@/pages/PublicReview";
import Leads from "@/pages/Leads";
import ChatWidget from "@/pages/ChatWidget";
import LandingPages from "@/pages/LandingPages";
import VoiceAgent from "@/pages/VoiceAgent";
import GrowthTools from "@/pages/GrowthTools";
import Attribution from "@/pages/Attribution";
import Snapshots from "@/pages/Snapshots";
import PublicShowcase from "@/pages/PublicShowcase";
import Reports from "@/pages/Reports";
import Prospecting from "@/pages/Prospecting";
import Settings from "@/pages/Settings";
import Agency from "@/pages/Agency";
import BillingSuccess from "@/pages/BillingSuccess";
import PublicVisibilityChecker from "@/pages/PublicVisibilityChecker";
import PublicAssessmentV2 from "@/pages/PublicAssessmentV2";
import ConsultationComplete from "@/pages/ConsultationComplete";
import ServicePage from "@/pages/ServicePage";
import RankingAlertFunnel from "@/pages/RankingAlertFunnel";
import Blueprint from "@/pages/Blueprint";
import AppShell from "@/components/AppShell";
import "@/App.css";


function CheckRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get("cid") ? <PublicVisibilityChecker /> : <PublicAssessmentV2 />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner text-zinc-400"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <Routes>
          <Route path="/" element={<LandingReconstruction />} />
    <Route path="/business/ranktrust" element={<BusinessProfile />} />
          <Route path="/business/:slug" element={<PublicClientProfile />} />
          <Route path="/reviews" element={<RankTrustReviewsDirectory />} />
          <Route path="/reviews/:slug" element={<PublicClientProfile />} />
          <Route path="/verified-businesses" element={<RankTrustReviewsDirectory />} />
          <Route path="/verified-businesses/:categorySlug" element={<RankTrustReviewsDirectory />} />
          <Route path="/trust-methodology" element={<TrustMethodology />} />
          <Route path="/playbook" element={<PlaybookLanding />} />
          <Route path="/playbook/download" element={<PlaybookDownload />} />
          <Route path="/r/:token" element={<PublicReview />} />
          <Route path="/widget/:token" element={<ChatWidget />} />
          <Route path="/agency/:slug" element={<PublicShowcase />} />
          <Route path="/check" element={<CheckRoute />} />
          <Route path="/check-v2" element={<PublicAssessmentV2 />} />
          <Route path="/consultation/complete" element={<ConsultationComplete />} />
          <Route path="/local-seo" element={<ServicePage slug="local-seo" />} />
          <Route path="/google-maps-ranking" element={<ServicePage slug="google-maps-ranking" />} />
          <Route path="/ai-search-visibility" element={<ServicePage slug="ai-search-visibility" />} />
          <Route path="/website-optimization" element={<ServicePage slug="website-optimization" />} />
          <Route path="/lead-follow-up" element={<ServicePage slug="lead-follow-up" />} />
          <Route path="/workflow-automation" element={<ServicePage slug="workflow-automation" />} />
          <Route path="/payments" element={<ServicePage slug="payments" />} />
          <Route path="/review-management" element={<ServicePage slug="review-management" />} />
          <Route path="/blueprint/:blueprintId" element={<Blueprint />} />
          <Route path="/rankingalert" element={<RankingAlertFunnel />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/presell/video-:step" element={<Presell />} />
          <Route path="/presell/:step" element={<Presell />} />
          <Route path="/presell/video-1" element={<Presell />} />
          <Route path="/presell/video-2" element={<Presell />} />
          <Route path="/presell/video-3" element={<Presell />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/a/:slug/demo" element={<Demo />} />
          <Route path="/prospecting" element={<Navigate to="/app/prospecting" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
          <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/playbook" element={<PlaybookManager />} />
            <Route path="/app/rank-tracker" element={<RankTracker />} />
            <Route path="/app/geo-grid" element={<GeoGrid />} />
            <Route path="/app/audit" element={<AIAudit />} />
            <Route path="/app/content" element={<ContentStudio />} />
            {process.env.REACT_APP_CONTENT_ENGINE_ENABLED === "true" && <Route path="/app/content-engine" element={<ContentEngine />} />}
            <Route path="/app/competitors" element={<Competitors />} />
            <Route path="/app/chatgpt-visibility" element={<ChatGPTVisibility />} />
            <Route path="/app/trust-profile" element={<BusinessTrustDashboard />} />
            <Route path="/app/trust-profile/new" element={<TrustProfileSetup />} />
            <Route path="/app/trust-editorial" element={<TrustEditorialQueue />} />
            <Route path="/app/reviews" element={<Reviews />} />
            <Route path="/app/review-moderation" element={<RankTrustReviewModeration />} />
            <Route path="/app/review-requests" element={<ReviewRequests />} />
            <Route path="/app/leads" element={<Leads />} />
            <Route path="/app/prospecting" element={<Prospecting />} />
            <Route path="/app/voice-agent" element={<VoiceAgent />} />
            <Route path="/app/landing-pages" element={<LandingPages />} />
            <Route path="/app/growth" element={<GrowthTools />} />
            <Route path="/app/attribution" element={<Attribution />} />
            <Route path="/app/snapshots" element={<Snapshots />} />
            <Route path="/app/reports" element={<Reports />} />
            <Route path="/app/agency" element={<Agency />} />
            <Route path="/app/settings" element={<Settings />} />
          </Route>
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
